import { Alert, Box, Button, Center, Flex, Image, Paper, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import axios from 'axios';
import { useApi } from 'api/api-context';
import LanguageMenu from 'components/LanguageMenu';
import useLang from 'lang';
import React, { FormEvent, useState } from 'react';
import theme from 'theme';

const OBJECT_DOES_NOT_EXIST_ERROR_CODE = 4300;
const INVALID_PASSWORD_ERROR_CODE = 4301;
const INVALID_TOTP_ERROR_CODE = 4313;
const LOGIN_INPUT_STYLES = {
  input: {
    border: `1px solid ${theme.colors?.gray?.[3]}`,
  },
};

/**
 * Renders the in-helpdesk login screen.
 */
export default function Login() {
  const { getAction, startSession } = useApi();
  const { _t } = useLang();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Authenticates the user without changing their current browser location.
   */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const login = getAction('Login');
      const response = await login({
        payload: {
          email: email.trim(),
          password,
          platform: 'helpdesk',
        },
      });

      if (!response.token) {
        throw new Error('Login response did not contain a token');
      }

      startSession(response.token);
    } catch (loginError) {
      if (axios.isAxiosError(loginError)) {
        const code = loginError.response?.data?.code;

        if (code === INVALID_TOTP_ERROR_CODE) {
          setError(_t('This account uses two-factor authentication and cannot sign in through this helpdesk.'));
        } else if (code === OBJECT_DOES_NOT_EXIST_ERROR_CODE || code === INVALID_PASSWORD_ERROR_CODE) {
          setError(_t('Incorrect email or password.'));
        } else {
          setError(_t('Unable to sign in. Check your connection and try again.'));
        }
      } else {
        setError(_t('Unable to sign in. Check your connection and try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box mih="100vh" bg={theme.colors?.gray?.[1]} p="1rem">
      <Flex justify="flex-end">
        <Box w="8rem">
          <LanguageMenu />
        </Box>
      </Flex>

      <Center mih="calc(100vh - 5rem)">
        <Paper w="100%" maw="26rem" p={{ base: '1.5rem', sm: '2.5rem' }} radius="lg" shadow="sm">
          <Stack gap="lg">
            <Image src="/img/phillip-logo.svg" alt={_t('Phillip logo')} h="3.5rem" fit="contain" />

            <Stack gap="xs" ta="center">
              <Text component="h1" size="xl" fw={600} c={theme.colors?.gray?.[12]}>
                {_t('Log in')}
              </Text>
              <Text c={theme.colors?.gray?.[9]}>{_t('Log in to access the Phillip helpdesk.')}</Text>
            </Stack>

            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                <TextInput
                  label={_t('Email')}
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.currentTarget.value)}
                  autoComplete="email"
                  required
                  disabled={loading}
                  styles={LOGIN_INPUT_STYLES}
                />
                <PasswordInput
                  label={_t('Password')}
                  value={password}
                  onChange={(event) => setPassword(event.currentTarget.value)}
                  autoComplete="current-password"
                  required
                  disabled={loading}
                  styles={LOGIN_INPUT_STYLES}
                />

                {error && (
                  <Alert color="red" role="alert">
                    {error}
                  </Alert>
                )}

                <Button type="submit" fullWidth size="lg" loading={loading}>
                  {_t('Log in')}
                </Button>
              </Stack>
            </form>
          </Stack>
        </Paper>
      </Center>
    </Box>
  );
}
